import { IconWithBackground } from '@/components/common/customization/IconWithBackground';
import { SelectableTile } from '@/components/common/customization/SelectableTile';
import { ColorSchemePreview } from '@/components/common/customization/previews/ColorSchemePreview';
import { FontFamilyPreview } from '@/components/common/customization/previews/FontFamilyPreview';
import { LayoutPreview } from '@/components/common/customization/previews/LayoutPreview';
import { LayoutWidthPreview } from '@/components/common/customization/previews/LayoutWidthPreview';
import { MenuDirectionPreview } from '@/components/common/customization/previews/MenuDirectionPreview';
import { ThemeModePreview } from '@/components/common/customization/previews/ThemeModePreview';
import { BORDERS, COLORS, SPACING, TYPOGRAPHY } from '@/constants/design';
import { useColors, useTheme } from '@/contexts/ThemeContext';
import { useGlobalStore } from '@/stores/globalStore';
import { COLOR_PALETTES } from '@/utils/colorUtils';
import {
  BgColorsOutlined,
  CloseOutlined,
  ColumnWidthOutlined,
  CompressOutlined,
  FontSizeOutlined,
  LayoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MoonOutlined,
  ReloadOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { Button, Col, Collapse, Drawer, Row, Slider, Typography } from 'antd';
import type { CollapseProps } from 'antd';
import type React from 'react';
import { useState } from 'react';

const { Text } = Typography;

interface CustomizationPanelProps {
  open: boolean;
  onClose: () => void;
}

const CustomizationPanel: React.FC<CustomizationPanelProps> = ({ open, onClose }) => {
  const {
    customization,
    setCustomization,
    theme: currentTheme,
    setTheme,
    sidebarWidth,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    layoutType,
    setLayoutType: setGlobalLayoutType,
  } = useGlobalStore();

  const [activePanels, setActivePanels] = useState<string[]>([]);

  // Use global layoutType
  const currentLayoutType = layoutType || (sidebarCollapsed ? 'mini' : 'vertical');
  const [menuDirection, setMenuDirection] = useState<'ltr' | 'rtl'>('ltr');
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'system'>(currentTheme);
  const colorScheme = customization.primaryColor || 'blue';
  const layoutWidth = customization.layoutWidth ?? 'container';
  // Use actual fontFamily from customization store
  const fontFamily = customization.fontFamily || 'default';

  const handlePanelChange = (keys: string | string[]) => {
    setActivePanels(Array.isArray(keys) ? keys : [keys]);
  };

  const handleLayoutChange = (value: string) => {
    const next = value as 'vertical' | 'mini';
    setGlobalLayoutType(next);
  };

  const handleMenuDirectionChange = (value: string) => {
    setMenuDirection(value as 'ltr' | 'rtl');
  };

  const handleThemeModeChange = (value: string) => {
    const mode = value as 'light' | 'dark' | 'system';
    setThemeMode(mode);
    if (mode !== 'system') {
      setTheme(mode);
    }
  };

  const handleColorSchemeChange = (value: string) => {
    setCustomization({ primaryColor: value });
  };

  const handleLayoutWidthChange = (value: string) => {
    setCustomization({ layoutWidth: value as 'fluid' | 'container' });
  };

  const handleFontFamilyChange = (value: string) => {
    setCustomization({ fontFamily: value as 'default' | 'serif' | 'monospace' });
  };

  const handleSidebarWidthChange = (value: number) => {
    setSidebarWidth(value);
  };

  const handleComponentSizeChange = (value: string) => {
    const size = value as 'compact' | 'middle' | 'large';
    setCustomization({
      density: size === 'compact' ? 'compact' : size === 'large' ? 'comfortable' : 'middle',
      compactMode: size === 'compact',
    });

    // Adjust sidebar width based on component size
    if (size === 'compact') {
      setSidebarWidth(220); // Smaller sidebar for compact mode
    } else if (size === 'large') {
      setSidebarWidth(280); // Larger sidebar for comfortable mode
    } else {
      setSidebarWidth(247); // Default sidebar width for middle mode
    }
  };

  const handleResetAll = () => {
    setGlobalLayoutType('vertical');
    setMenuDirection('ltr');
    setThemeMode('light');
    setTheme('light');
    setSidebarWidth(247);
    setSidebarCollapsed(false);
    setCustomization({
      fontSize: 'medium',
      fontFamily: 'default',
      compactMode: false,
      density: 'middle',
      primaryColor: 'blue',
      layoutWidth: 'container',
    });
  };

  // Use semantic tokens - single source of truth, auto-adapts to theme!
  const tokens = useColors();
  const { theme } = useTheme();
  const palette = COLOR_PALETTES[colorScheme as keyof typeof COLOR_PALETTES] || COLOR_PALETTES.blue;
  const primaryColor = palette[500];
  const primaryBorderColor = palette[400] || primaryColor;

  // Selected tile background using semantic tokens:
  // - Light mode: soft pastel from palette[50]
  // - Dark mode: mix primary with layout background so color is visible but not blinding
  const primarySoftBg =
    theme === 'dark'
      ? `color-mix(in srgb, ${primaryColor} 26%, ${tokens.surface.layout})`
      : palette[50] || tokens.primary[50];

  const collapseItems: CollapseProps['items'] = [
    {
      key: '1',
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.xs,
            padding: `${SPACING.xxs} 0`,
          }}
        >
          <IconWithBackground icon={<LayoutOutlined />} color={colorScheme} />
          <div>
            <Text
              strong
              style={{
                display: 'block',
                fontSize: TYPOGRAPHY.fontSize.sm,
                lineHeight: 1.4,
                color: tokens.text.primary,
              }}
            >
              Theme Layout
            </Text>
            <Text
              style={{
                fontSize: TYPOGRAPHY.fontSize.xs,
                color: tokens.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Choose your layout
            </Text>
          </div>
        </div>
      ),
      children: (
        <div role="radiogroup" aria-label="Theme layout" style={{ marginTop: SPACING.xxs }}>
          <Row gutter={[4, 4]}>
            <Col span={8}>
              <SelectableTile
                value="vertical"
                selected={currentLayoutType === 'vertical'}
                onChange={handleLayoutChange}
                preview={<LayoutPreview type="vertical" size={40} />}
                title="Default"
                name="layout"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
            <Col span={8}>
              <SelectableTile
                value="mini"
                selected={currentLayoutType === 'mini'}
                onChange={handleLayoutChange}
                preview={<LayoutPreview type="mini" size={40} />}
                title="Mini Drawer"
                name="layout"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.xs,
            padding: `${SPACING.xxs} 0`,
          }}
        >
          <IconWithBackground icon={<MenuOutlined />} color={colorScheme} />
          <div>
            <Text strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}>
              Menu Direction
            </Text>
            <Text
              style={{
                fontSize: '12px',
                color: tokens.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Choose RTL or LTR menu direction
            </Text>
          </div>
        </div>
      ),
      children: (
        <div role="radiogroup" aria-label="Menu direction" style={{ marginTop: SPACING.xxs }}>
          <Row gutter={[4, 4]}>
            <Col span={8}>
              <SelectableTile
                value="ltr"
                selected={menuDirection === 'ltr'}
                onChange={handleMenuDirectionChange}
                preview={<MenuDirectionPreview direction="ltr" size={40} />}
                title="LTR"
                name="menuDirection"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
            <Col span={8}>
              <SelectableTile
                value="rtl"
                selected={menuDirection === 'rtl'}
                onChange={handleMenuDirectionChange}
                preview={<MenuDirectionPreview direction="rtl" size={40} />}
                title="RTL"
                name="menuDirection"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: '3',
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.xs,
            padding: `${SPACING.xxs} 0`,
          }}
        >
          <IconWithBackground
            icon={themeMode === 'dark' ? <MoonOutlined /> : <SunOutlined />}
            color={colorScheme}
          />
          <div>
            <Text strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}>
              Theme Mode
            </Text>
            <Text
              style={{
                fontSize: '12px',
                color: tokens.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Choose light, dark or system mode
            </Text>
          </div>
        </div>
      ),
      children: (
        <div role="radiogroup" aria-label="Theme mode" style={{ marginTop: SPACING.xxs }}>
          <Row gutter={[4, 4]}>
            <Col span={8}>
              <SelectableTile
                value="light"
                selected={themeMode === 'light'}
                onChange={handleThemeModeChange}
                preview={<ThemeModePreview mode="light" size={40} />}
                title="Light"
                name="themeMode"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
            <Col span={8}>
              <SelectableTile
                value="dark"
                selected={themeMode === 'dark'}
                onChange={handleThemeModeChange}
                preview={<ThemeModePreview mode="dark" size={40} />}
                title="Dark"
                name="themeMode"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
            <Col span={8}>
              <SelectableTile
                value="system"
                selected={themeMode === 'system'}
                onChange={handleThemeModeChange}
                preview={<ThemeModePreview mode="system" size={40} />}
                title="System"
                name="themeMode"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: '4',
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.xs,
            padding: `${SPACING.xxs} 0`,
          }}
        >
          <IconWithBackground icon={<BgColorsOutlined />} color={colorScheme} />
          <div>
            <Text strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}>
              Color Scheme
            </Text>
            <Text
              style={{
                fontSize: '12px',
                color: tokens.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Choose your primary theme color
            </Text>
          </div>
        </div>
      ),
      children: (
        <div role="radiogroup" aria-label="Color scheme" style={{ marginTop: SPACING.xxs }}>
          <Row gutter={[4, 4]}>
            {['blue', 'green', 'red', 'yellow', 'purple', 'pink'].map((color) => (
              <Col span={8} key={color}>
                <SelectableTile
                  value={color}
                  selected={colorScheme === color}
                  onChange={handleColorSchemeChange}
                  preview={<ColorSchemePreview color={color} size={40} />}
                  title={color.charAt(0).toUpperCase() + color.slice(1)}
                  name="colorScheme"
                  accentColor={primaryBorderColor}
                  accentBg={primarySoftBg}
                />
              </Col>
            ))}
          </Row>
        </div>
      ),
    },
    {
      key: '5',
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.xs,
            padding: `${SPACING.xxs} 0`,
          }}
        >
          <IconWithBackground icon={<ColumnWidthOutlined />} color={colorScheme} />
          <div>
            <Text strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}>
              Layout Width
            </Text>
            <Text
              style={{
                fontSize: '12px',
                color: tokens.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Choose fluid or container layout
            </Text>
          </div>
        </div>
      ),
      children: (
        <div role="radiogroup" aria-label="Layout width" style={{ marginTop: SPACING.xxs }}>
          <Row gutter={[4, 4]}>
            <Col span={8}>
              <SelectableTile
                value="fluid"
                selected={layoutWidth === 'fluid'}
                onChange={handleLayoutWidthChange}
                preview={<LayoutWidthPreview width="fluid" size={40} />}
                title="Fluid"
                name="layoutWidth"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
            <Col span={8}>
              <SelectableTile
                value="container"
                selected={layoutWidth === 'container'}
                onChange={handleLayoutWidthChange}
                preview={<LayoutWidthPreview width="container" size={40} />}
                title="Container"
                name="layoutWidth"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: '6',
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.xs,
            padding: `${SPACING.xxs} 0`,
          }}
        >
          <IconWithBackground icon={<FontSizeOutlined />} color={colorScheme} />
          <div>
            <Text strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}>
              Font Family
            </Text>
            <Text
              style={{
                fontSize: '12px',
                color: tokens.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Choose your font family.
            </Text>
          </div>
        </div>
      ),
      children: (
        <div role="radiogroup" aria-label="Font family" style={{ marginTop: SPACING.xxs }}>
          <Row gutter={[4, 4]}>
            {[
              { value: 'default', label: 'System Sans' },
              { value: 'serif', label: 'Serif' },
              { value: 'monospace', label: 'Monospace' },
            ].map((font) => (
              <Col span={8} key={font.value}>
                <SelectableTile
                  value={font.value}
                  selected={fontFamily === font.value}
                  onChange={handleFontFamilyChange}
                  preview={
                    <FontFamilyPreview
                      family={font.value as 'default' | 'serif' | 'monospace'}
                      size={40}
                    />
                  }
                  title={font.label}
                  name="fontFamily"
                  accentColor={primaryBorderColor}
                  accentBg={primarySoftBg}
                />
              </Col>
            ))}
          </Row>
        </div>
      ),
    },
    {
      key: '7',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <IconWithBackground icon={<MenuFoldOutlined />} color={colorScheme} />
          <div>
            <Text strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}>
              Sidebar Width
            </Text>
            <Text
              style={{
                fontSize: '12px',
                color: tokens.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Adjust sidebar width
            </Text>
          </div>
        </div>
      ),
      children: (
        <div style={{ marginTop: '8px', padding: '8px 0' }}>
          <div
            style={{
              marginBottom: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: tokens.text.secondary }}>
              Width
            </Text>
            <Text strong style={{ fontSize: TYPOGRAPHY.fontSize.sm, color: primaryColor }}>
              {sidebarWidth}px
            </Text>
          </div>
          <Slider
            min={200}
            max={400}
            value={sidebarWidth}
            onChange={handleSidebarWidthChange}
            tooltip={{ formatter: (value) => `${value}px` }}
            marks={{
              200: '200',
              280: '280',
              320: '320',
              400: '400',
            }}
          />
        </div>
      ),
    },
    {
      key: '8',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <IconWithBackground icon={<CompressOutlined />} color={colorScheme} />
          <div>
            <Text strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}>
              Component Size
            </Text>
            <Text
              style={{
                fontSize: '12px',
                color: tokens.text.secondary,
                lineHeight: 1.4,
              }}
            >
              Choose compact or comfortable spacing
            </Text>
          </div>
        </div>
      ),
      children: (
        <div role="radiogroup" aria-label="Component size" style={{ marginTop: SPACING.xxs }}>
          <Row gutter={[4, 4]}>
            <Col span={8}>
              <SelectableTile
                value="compact"
                selected={customization.compactMode || customization.density === 'compact'}
                onChange={handleComponentSizeChange}
                preview={
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      padding: 4,
                      border: `1px solid ${tokens.border.primary}`,
                      borderRadius: 4,
                      background: tokens.surface.hover,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      justifyContent: 'center',
                    }}
                  >
                    <div style={{ height: 8, background: COLORS.primary[300], borderRadius: 3 }} />
                    <div style={{ height: 8, background: COLORS.primary[300], borderRadius: 3 }} />
                    <div style={{ height: 8, background: COLORS.primary[300], borderRadius: 3 }} />
                  </div>
                }
                title="Compact"
                name="componentSize"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
            <Col span={8}>
              <SelectableTile
                value="middle"
                selected={!customization.compactMode && customization.density === 'middle'}
                onChange={handleComponentSizeChange}
                preview={
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      padding: 4,
                      border: `1px solid ${tokens.border.primary}`,
                      borderRadius: 4,
                      background: tokens.surface.hover,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      justifyContent: 'center',
                    }}
                  >
                    <div style={{ height: 10, background: COLORS.primary[300], borderRadius: 3 }} />
                    <div style={{ height: 10, background: COLORS.primary[300], borderRadius: 3 }} />
                    <div style={{ height: 10, background: COLORS.primary[300], borderRadius: 3 }} />
                  </div>
                }
                title="Middle"
                name="componentSize"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
            <Col span={8}>
              <SelectableTile
                value="large"
                selected={!customization.compactMode && customization.density === 'comfortable'}
                onChange={handleComponentSizeChange}
                preview={
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      padding: 4,
                      border: `1px solid ${tokens.border.primary}`,
                      borderRadius: 4,
                      background: tokens.surface.hover,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      justifyContent: 'center',
                    }}
                  >
                    <div style={{ height: 12, background: COLORS.primary[300], borderRadius: 3 }} />
                    <div style={{ height: 12, background: COLORS.primary[300], borderRadius: 3 }} />
                    <div style={{ height: 12, background: COLORS.primary[300], borderRadius: 3 }} />
                  </div>
                }
                title="Comfortable"
                name="componentSize"
                accentColor={primaryBorderColor}
                accentBg={primarySoftBg}
              />
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  // Use a darker shade (600 or 700) for header to ensure white text is always visible
  const headerBg = palette[600] || palette[700] || primaryColor;

  return (
    <Drawer
      title={
        <Text strong style={{ color: '#ffffff', fontSize: '16px', fontWeight: 600 }}>
          Theme Customization
        </Text>
      }
      placement="right"
      width={360}
      open={open}
      onClose={onClose}
      closable={false}
      styles={{
        body: { padding: 0, background: tokens.surface.layout },
        header: {
          borderBottom: 'none',
          background: headerBg,
          padding: `${SPACING.sm} ${SPACING.md}`,
        },
        wrapper: {
          zIndex: 1400,
        },
      }}
      extra={
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{ color: '#ffffff', fontSize: '16px' }}
          aria-label="Close settings"
        />
      }
    >
      <div
        style={{
          padding: `${SPACING.xs} ${SPACING.xs}`,
          background: tokens.surface.layout,
        }}
      >
        <Collapse
          activeKey={activePanels}
          onChange={handlePanelChange}
          expandIconPosition="end"
          style={{
            background: 'transparent',
            border: 'none',
          }}
          items={collapseItems}
        />

        <div
          style={{
            marginTop: SPACING.sm,
            paddingTop: SPACING.xs,
            borderTop: `${BORDERS.width.thin} solid ${tokens.border.primary}`,
          }}
        >
          <Button
            block
            onClick={handleResetAll}
            icon={<ReloadOutlined />}
            style={{
              height: '36px',
              borderRadius: '6px',
            }}
          >
            Reset All
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default CustomizationPanel;
